/**
 * `array-index`: the boxes of an array with their indices underneath, stepped
 * one index at a time so `marks[2]` stops being a guess.
 *
 * <div class="pc-widget" data-widget="array-index" data-name="marks"
 *      data-values="[91,78,65,40]" data-active="2"
 *      data-caption="The index counts from zero, so marks[2] is the third box.">
 *   <p class="pc-widget-fallback">marks holds 91, 78, 65, 40.</p>
 * </div>
 *
 * Named cursors (`LN-WGT02`, for two-pointer walks): `data-cursors` is a JSON
 * list of one or two cursor names, and `data-steps` the walk, a JSON list of
 * `{ "at": [index, index], "note"?: string }`, one index per cursor, `note`
 * one sentence in the lesson's language. The stepper then plays the steps
 * instead of the indexes, the first frame being step 1.
 *
 * <div class="pc-widget" data-widget="array-index" data-name="prices"
 *      data-values="[2,3,5,7,8,11]" data-cursors="[&quot;left&quot;,&quot;right&quot;]"
 *      data-steps="[{&quot;at&quot;:[0,5],&quot;note&quot;:&quot;2 + 11 = 13 is too small.&quot;},{&quot;at&quot;:[1,5]}]">
 *   <p class="pc-widget-fallback">left starts at 0 and right at 5.</p>
 * </div>
 *
 * A block without `data-cursors` is the single-index widget it always was;
 * `data-cursors` without valid `data-steps` (or an index outside the array)
 * leaves the fallback.
 */

import {
  asText,
  attachStepper,
  buildShell,
  el,
  isIndex,
  isRecord,
  isStringOrNumber,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

const isValues = (value: unknown): value is (string | number)[] =>
  Array.isArray(value) && value.length > 0 && value.every(isStringOrNumber);

const isCursors = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length >= 1 &&
  value.length <= 2 &&
  value.every((name) => typeof name === "string" && name.length > 0);

interface CursorStep {
  at: number[];
  note?: string;
}

const isCursorSteps =
  (cursors: number, length: number) =>
  (value: unknown): value is CursorStep[] =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (step) =>
        isRecord(step) &&
        Array.isArray(step.at) &&
        step.at.length === cursors &&
        step.at.every((index) => isIndex(index) && index < length) &&
        (step.note === undefined || typeof step.note === "string"),
    );

export function renderArrayIndex(host: HTMLElement, text: WidgetText): boolean {
  const values = parseJson(host.dataset.values, isValues);
  if (!values) return false;
  const name = host.dataset.name ?? "a";

  let cursors: string[] | null = null;
  let walk: CursorStep[] | null = null;
  if (host.dataset.cursors !== undefined) {
    cursors = parseJson(host.dataset.cursors, isCursors);
    walk = cursors ? parseJson(host.dataset.steps, isCursorSteps(cursors.length, values.length)) : null;
    if (!cursors || !walk) return false;
  }

  const shell = buildShell(host, {
    label: text("labelArray"),
    caption: host.dataset.caption,
  });

  const row = el("div", "pc-widget-cells pc-widget-cells--tight");
  const marks: HTMLSpanElement[] = [];
  const boxes = values.map((value, index) => {
    const box = el("div", "pc-cell");
    box.append(el("span", "pc-cell-value", asText(value)));
    box.append(el("span", "pc-cell-meta", `[${index}]`));
    if (cursors) {
      const mark = el("span", "pc-cell-cursor");
      box.append(mark);
      marks.push(mark);
    }
    row.append(box);
    return box;
  });
  shell.body.append(row);

  const readout = liveNote("pc-widget-caption");
  shell.body.append(readout);

  const read = (index: number) => text("arrayRead", { expr: `${name}[${index}]`, value: asText(values[index]) });

  if (cursors && walk) {
    const names = cursors;
    const steps = walk;
    attachStepper(
      shell,
      steps.length,
      (step) => {
        const at = steps[step].at;
        boxes.forEach((box, index) => {
          box.classList.toggle("is-active", at[0] === index);
          box.classList.toggle("is-active-2", at.length > 1 && at[1] === index && at[0] !== index);
          marks[index].textContent = names.filter((_, k) => at[k] === index).join(" ");
        });
        readout.textContent = steps[step].note ?? at.map(read).join("; ");
      },
      text,
      { liveReadout: false },
    );
    return true;
  }

  attachStepper(
    shell,
    values.length,
    (step) => {
      boxes.forEach((box, index) => box.classList.toggle("is-active", index === step));
      readout.textContent = read(step);
    },
    text,
    { stepLabel: (step) => `[${step}]`, initialStep: Number(host.dataset.active ?? 0), liveReadout: false },
  );
  return true;
}
